import { useState } from 'react';
import { Share2, Twitter, Facebook, Mail, Link as LinkIcon, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from '@/hooks/use-toast';
import {
  shareToTwitter,
  shareToFacebook,
  shareNative,
  copyToClipboard,
  shareViaEmail,
  generateShareContent,
} from '@/lib/shareUtils';

interface ExternalShareButtonProps {
  shareType: 'review' | 'milestone' | 'profile' | 'book' | 'streak';
  data: any;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  children?: React.ReactNode;
}

export function ExternalShareButton({
  shareType,
  data,
  variant = 'outline',
  size = 'sm',
  className,
  children
}: ExternalShareButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareContent = generateShareContent(shareType, data);

  const handleTwitterShare = () => {
    shareToTwitter(shareContent);
    setIsOpen(false);
  };

  const handleFacebookShare = () => {
    shareToFacebook(shareContent.url);
    setIsOpen(false);
  };

  const handleNativeShare = async () => {
    const success = await shareNative(shareContent);
    if (success) {
      setIsOpen(false);
    } else {
      toast({
        title: 'Share not available',
        description: 'Please use one of the other sharing options.',
        variant: 'destructive',
      });
    }
  };

  const handleCopyLink = async () => {
    const success = await copyToClipboard(shareContent.url);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: 'Link copied!',
        description: 'The link has been copied to your clipboard.',
      });
    } else {
      toast({
        title: 'Failed to copy',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleEmailShare = () => {
    shareViaEmail(shareContent);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant={variant} size={size} className={className}>
          <Share2 className="w-4 h-4 mr-2" />
          {children || 'Share'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="end">
        <div className="flex flex-col gap-1">
          {/* Native Share (mobile) */}
          {navigator.share && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start"
              onClick={handleNativeShare}
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          )}

          {/* Twitter */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={handleTwitterShare}
          >
            <Twitter className="w-4 h-4 mr-2" />
            Share on X
          </Button>

          {/* Facebook */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={handleFacebookShare}
          >
            <Facebook className="w-4 h-4 mr-2" />
            Share on Facebook
          </Button>

          {/* Email */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={handleEmailShare}
          >
            <Mail className="w-4 h-4 mr-2" />
            Share via Email
          </Button>

          {/* Copy Link */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={handleCopyLink}
          >
            {copied ? (
              <Check className="w-4 h-4 mr-2 text-success" />
            ) : (
              <LinkIcon className="w-4 h-4 mr-2" />
            )}
            {copied ? 'Copied!' : 'Copy Link'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
